import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircleOutlined';
import { CenteredAuthCard } from './CenteredAuthCard';
import { PersonAvatar } from '../components/shared/PersonAvatar';

export interface StateSuccessProps {
  /** Signed-in user's avatar URL — shown in the circle in place of the tick. */
  avatarUrl?: string;
  /** Signed-in user's name — drives the avatar's initials fallback. */
  name?: string;
}

/** SSO callback success state — shown while redirecting to /dashboard. */
export function StateSuccess({ avatarUrl, name }: StateSuccessProps = {}) {
  return (
    <CenteredAuthCard>
      {avatarUrl ? (
        // Greet the user with their own picture when they have one; the token is
        // already in localStorage at this point, so PersonAvatar can fetch it.
        <Box sx={{ display: 'inline-flex', mx: 'auto', mb: 0.5 }}>
          <PersonAvatar name={name} src={avatarUrl} size={56} />
        </Box>
      ) : (
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'statusSoft.ok',
            color: 'success.main',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 0.5,
          }}
        >
          <CheckIcon sx={{ fontSize: 28 }} />
        </Box>
      )}

      <Typography
        component="h1"
        sx={{ fontSize: '1.375rem', fontWeight: 500, letterSpacing: '-0.025em', textAlign: 'center' }}
      >
        You&apos;re in.
      </Typography>

      <Typography sx={{ fontSize: '0.84375rem', color: 'text.secondary', textAlign: 'center', lineHeight: 1.5 }}>
        Signed in successfully.{' '}
        <Box component="span" sx={{ color: 'text.primary', fontWeight: 500, fontSize: 'inherit' }}>
          Taking you to your workspace…
        </Box>
      </Typography>

      <Box
        role="progressbar"
        aria-label="Redirecting to your workspace"
        sx={{
          position: 'relative',
          width: '100%',
          height: 4,
          bgcolor: 'surface.sunk',
          borderRadius: '4px',
          overflow: 'hidden',
          mt: 0.5,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '40%',
            bgcolor: 'primary.main',
            borderRadius: '4px',
            '@keyframes ppSuccessBar': {
              '0%': { transform: 'translateX(-100%)' },
              '100%': { transform: 'translateX(350%)' },
            },
            animation: 'ppSuccessBar 1.1s ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              width: '72%',
            },
          }}
        />
      </Box>
    </CenteredAuthCard>
  );
}
